/**
 * Adapter único de estatísticas da inbox (abas, badge, previews).
 *
 * `ConversationList` e `useUnreadNotifications` só devem falar com este módulo:
 * ele decide entre V1 (RPCs de contagem) e V2 (read model incremental) conforme
 * o rollout, e roda a comparação em shadow quando habilitada.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { fetchConversationInboxCounts } from "./conversation-inbox-counts";
import { fetchWorkspaceUnreadInboxTotal } from "./unread-inbox-total";
import {
  fetchWorkspaceUnreadInboxPreviews,
  type UnreadInboxPreviewRow,
} from "./unread-inbox-previews";
import { isUnassignedIntegrationFilter } from "@/lib/conversation-integration-assign";
import {
  deriveTabCountsFromMatrix,
  deriveUnreadTotalFromMatrix,
  type InboxChannel,
  type InboxStatsRow,
  type InboxTabCounts,
  type InboxTabCountsParams,
} from "./inbox-stats-derive";
import {
  inboxStatsShadowSampleRate,
  isInboxStatsShadowEnabled,
  isInboxStatsV2Enabled,
} from "./inbox-stats-flags";

export type {
  InboxChannel,
  InboxStatsRow,
  InboxTabCounts,
  InboxTabCountsParams,
  InboxMatrixScope,
} from "./inbox-stats-derive";
export { deriveTabCountsFromMatrix, deriveUnreadTotalFromMatrix, hasUsableMatrixRows } from "./inbox-stats-derive";
export type { UnreadInboxPreviewRow };

export {
  fetchChannelUnassignedCounts,
  fetchChannelConversationCounts,
  fetchIntegrationConversationCount,
  hardInvalidateInboxChannelCounts,
  softInvalidateInboxChannelCounts,
  noteConversationChangeForChannelCounts,
  
  INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT,
  type InboxChannelCountsInvalidatedDetail,
} from "./inbox-channel-counts";

type RolloutMode = "off" | "shadow" | "v2";

type Rollout = { mode: RolloutMode; sampleRate: number };

let rolloutCache: { value: Rollout; at: number } | null = null;
const ROLLOUT_TTL_MS = 60_000;

/** Rollout do servidor (singleton), com cache curto; falha → 'off'. */
async function getRollout(client: SupabaseClient<Database>): Promise<Rollout> {
  if (rolloutCache && Date.now() - rolloutCache.at < ROLLOUT_TTL_MS) return rolloutCache.value;

  let value: Rollout = { mode: "off", sampleRate: inboxStatsShadowSampleRate() };
  try {
    const { data, error } = await (
      client.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: unknown; error: unknown }>
    )("get_inbox_stats_rollout");
    if (!error && data) {
      const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
      const mode = row?.mode;
      if (mode === "off" || mode === "shadow" || mode === "v2") {
        value = {
          mode,
          sampleRate: Number(row?.shadow_sample_rate ?? value.sampleRate) || value.sampleRate,
        };
      }
    }
  } catch {
    /* mantém 'off' */
  }

  rolloutCache = { value, at: Date.now() };
  return value;
}

function v2Enabled(rollout: Rollout): boolean {
  return isInboxStatsV2Enabled() || rollout.mode === "v2";
}

function shadowEnabled(rollout: Rollout): boolean {
  return isInboxStatsShadowEnabled() || rollout.mode === "shadow";
}

function shouldSample(rollout: Rollout): boolean {
  const rate = isInboxStatsShadowEnabled() ? inboxStatsShadowSampleRate() : rollout.sampleRate;
  return Math.random() < rate;
}

function mapMatrixRow(row: Record<string, unknown>): InboxStatsRow {
  return {
    channel: String(row.channel ?? "") as InboxChannel,
    integrationId: (row.integration_id as string | null) ?? null,
    open: Number(row.open_count ?? 0),
    closed: Number(row.closed_count ?? 0),
    unreadOpen: Number(row.unread_open_count ?? 0),
    unreadClosed: Number(row.unread_closed_count ?? 0),
    mine: Number(row.mine_count ?? 0),
    attention: Number(row.attention_count ?? 0),
    ia: Number(row.ia_count ?? 0),
    mineClosed: Number(row.mine_closed_count ?? 0),
    attentionClosed: Number(row.attention_closed_count ?? 0),
    iaClosed: Number(row.ia_closed_count ?? 0),
  };
}

export async function fetchInboxStatsMatrix(
  client: SupabaseClient<Database>,
  workspaceId: string,
): Promise<InboxStatsRow[]> {
  const { data, error } = await (
    client.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: unknown }>
  )("get_inbox_stats_v2", { p_workspace_id: workspaceId });

  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(mapMatrixRow);
}

async function fetchTabCountsV1(
  client: SupabaseClient<Database>,
  params: InboxTabCountsParams,
): Promise<InboxTabCounts> {
  const result = await fetchConversationInboxCounts(client, {
    workspaceId: params.workspaceId,
    channel: params.channel,
    integrationId: params.integrationId,
    statusFilter: params.statusFilter,
    memberId: params.memberId,
  });
  return { ...result.filterCounts, listTotal: result.listTotal };
}

function scopeOf(params: InboxTabCountsParams) {
  const filterUnassigned = isUnassignedIntegrationFilter(params.integrationId);
  return {
    channel: params.channel,
    integrationId: filterUnassigned ? null : (params.integrationId ?? null),
    filterUnassigned,
    statusFilter: params.statusFilter,
  };
}

/** Compara V1 x V2 sem afetar a UI; só campos confiáveis na onda atual. */
function logShadowDiff(
  key: Record<string, unknown>,
  v1: InboxTabCounts,
  v2: InboxTabCounts,
): void {
  const fields: (keyof InboxTabCounts)[] = ["open", "closed", "all", "listTotal", "unread"];
  const diffs = fields
    .filter((f) => Number(v1[f] ?? 0) !== Number(v2[f] ?? 0))
    .map((f) => `${String(f)}: v1=${v1[f]} v2=${v2[f]}`);

  if (diffs.length > 0) {
    console.warn("[inbox-stats][shadow] divergência", { ...key, diffs });
  }
}

export async function fetchInboxTabCounts(
  client: SupabaseClient<Database>,
  params: InboxTabCountsParams,
): Promise<InboxTabCounts> {
  const rollout = await getRollout(client);
  const scope = scopeOf(params);

  if (v2Enabled(rollout)) {
    try {
      const matrix = await fetchInboxStatsMatrix(client, params.workspaceId);
      // Matriz vazia = backfill ausente: não mostrar zeros falsos.
      if (matrix.length > 0) return deriveTabCountsFromMatrix(matrix, scope);
      console.warn("[inbox-stats] matriz V2 vazia, usando V1", { workspaceId: params.workspaceId });
    } catch (error) {
      console.warn("[inbox-stats] falha no V2, usando V1", error);
    }
    return fetchTabCountsV1(client, params);
  }

  const v1 = await fetchTabCountsV1(client, params);

  if (shadowEnabled(rollout) && shouldSample(rollout)) {
    void fetchInboxStatsMatrix(client, params.workspaceId)
      .then((matrix) => {
        if (matrix.length === 0) return;
        logShadowDiff(
          { workspaceId: params.workspaceId, channel: params.channel, status: params.statusFilter },
          v1,
          deriveTabCountsFromMatrix(matrix, scope),
        );
      })
      .catch(() => {
        /* shadow nunca afeta a UI */
      });
  }

  return v1;
}

export async function fetchInboxUnreadTotal(
  client: SupabaseClient<Database>,
  workspaceId: string,
): Promise<number> {
  const rollout = await getRollout(client);

  if (v2Enabled(rollout)) {
    try {
      const matrix = await fetchInboxStatsMatrix(client, workspaceId);
      if (matrix.length > 0) return deriveUnreadTotalFromMatrix(matrix);
    } catch (error) {
      console.warn("[inbox-stats] falha no total V2, usando V1", error);
    }
    return fetchWorkspaceUnreadInboxTotal(client, workspaceId);
  }

  const v1 = await fetchWorkspaceUnreadInboxTotal(client, workspaceId);

  if (shadowEnabled(rollout) && shouldSample(rollout)) {
    void fetchInboxStatsMatrix(client, workspaceId)
      .then((matrix) => {
        if (matrix.length === 0) return;
        const v2 = deriveUnreadTotalFromMatrix(matrix);
        if (v2 !== v1) {
          console.warn("[inbox-stats][shadow] divergência unread total", {
            workspaceId,
            v1,
            v2,
          });
        }
      })
      .catch(() => {
        /* shadow nunca afeta a UI */
      });
  }

  return v1;
}

export function fetchInboxUnreadPreviews(
  client: SupabaseClient<Database>,
  workspaceId: string,
): Promise<UnreadInboxPreviewRow[]> {
  return fetchWorkspaceUnreadInboxPreviews(client, workspaceId);
}

export type InboxStatsHeartbeatParams = {
  workspaceId: string;
  memberId: string;
  channel: InboxChannel;
  integrationId?: string | null;
};

/**
 * "Token de mudança" da inbox no escopo atual (membro + canal + integração).
 * Lookup por PK quando há integração específica; senão max(updated_at) no canal.
 */
export async function fetchInboxStatsHeartbeat(
  client: SupabaseClient<Database>,
  params: InboxStatsHeartbeatParams,
): Promise<string | null> {
  const { workspaceId, memberId, channel } = params;
  const integrationId = params.integrationId;

  let query = client
    .from("workspace_member_inbox_stats" as never)
    .select("updated_at")
    .eq("workspace_id", workspaceId)
    .eq("member_id", memberId)
    .eq("channel", channel);

  if (integrationId && isUnassignedIntegrationFilter(integrationId)) {
    query = query.is("integration_id", null) as typeof query;
  } else if (integrationId) {
    query = query.eq("integration_id", integrationId) as typeof query;
  } else {
    const { data, error } = await query
      .order("updated_at", { ascending: false })
      .limit(1);
    if (error) return null;
    const row = (data as { updated_at?: string }[] | null)?.[0];
    return row?.updated_at ?? null;
  }

  const { data, error } = await query.maybeSingle();
  if (error) return null;
  return (data as { updated_at?: string } | null)?.updated_at ?? null;
}
